import { InlineMessage, Spinner } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { useLogTime } from './logTime';
import { MonthGrid } from './MonthGrid';
import type { Scope } from './model';
import { fetchTimeMonths, type LoggedMonth, type WorkBootstrap } from './workApi';

/** Hours logged by month and person over the last twelve months, read-only. Time is logged from Log time. */
export function WorkTimesheet({ boot, scope }: { boot: WorkBootstrap; scope: Scope }): JSX.Element {
  const [rows, setRows] = useState<LoggedMonth[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { savedAt } = useLogTime();
  const key = scope.kind === 'project' ? scope.projectId : scope.kind;

  useEffect(() => {
    let live = true;
    setError(null);
    fetchTimeMonths(authenticatedFetch, scope.kind === 'project' ? { project: scope.projectId } : { mine: scope.kind === 'mine' })
      .then((list) => live && setRows(list))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the hours.'));
    return () => {
      live = false;
    };
  }, [key, savedAt]);

  if (error) return <div className="pt-4"><InlineMessage tone="error" role="alert">{error}</InlineMessage></div>;
  if (!rows) return <div className="flex justify-center py-10"><Spinner label="Loading hours" /></div>;

  const names = new Map(boot.people.map((p) => [p.id, p.name]));
  const people = [...new Set(rows.map((r) => r.owner_id))]
    .map((id) => ({ key: id, label: names.get(id) ?? 'Someone' }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const months = [...new Set(rows.map((r) => r.month))].sort().reverse();
  const cell = new Map(rows.map((r) => [`${r.month}|${r.owner_id}`, r]));

  return (
    <div className="pt-4">
      <MonthGrid
        caption="Hours logged by month and person, the last twelve months"
        months={months}
        people={people}
        hours={(month, person) => cell.get(`${month}|${person}`)?.hours ?? 0}
        empty="No time logged in the last twelve months."
      />
    </div>
  );
}
